export type SearchHighlightSegment = {
    text: string;
    isMatch: boolean;
};

/**
 * Builds a compact, case-insensitive, literal search snippet with match
 * segments that can be rendered with a visual highlight.
 */
export function getSearchHighlightSegments(
    text: string | null | undefined,
    query: string,
): SearchHighlightSegment[] {
    const normalizedText = text?.replace(/[\r\n]+/g, ' ') ?? '';
    const normalizedQuery = query.trim();

    if (!normalizedText || !normalizedQuery) {
        return [{ text: normalizedText, isMatch: false }];
    }

    const lowercaseText = normalizedText.toLocaleLowerCase();
    const lowercaseQuery = normalizedQuery.toLocaleLowerCase();
    const firstMatchIndex = lowercaseText.indexOf(lowercaseQuery);

    if (firstMatchIndex === -1) {
        const preview = normalizedText.slice(0, 80);
        return [{
            text: preview.length < normalizedText.length ? `${preview}…` : preview,
            isMatch: false,
        }];
    }

    const start = Math.max(0, firstMatchIndex - 30);
    const end = Math.min(
        normalizedText.length,
        firstMatchIndex + normalizedQuery.length + 60,
    );
    const segments: SearchHighlightSegment[] = [];
    let cursor = start;
    let matchIndex = lowercaseText.indexOf(lowercaseQuery, cursor);

    if (start > 0) {
        segments.push({ text: '…', isMatch: false });
    }

    while (matchIndex !== -1 && matchIndex < end) {
        if (matchIndex > cursor) {
            segments.push({ text: normalizedText.slice(cursor, matchIndex), isMatch: false });
        }

        const matchEnd = matchIndex + normalizedQuery.length;
        segments.push({ text: normalizedText.slice(matchIndex, matchEnd), isMatch: true });
        cursor = matchEnd;
        matchIndex = lowercaseText.indexOf(lowercaseQuery, cursor);
    }

    if (cursor < end) {
        segments.push({ text: normalizedText.slice(cursor, end), isMatch: false });
    }

    if (end < normalizedText.length) {
        segments.push({ text: '…', isMatch: false });
    }

    return segments;
}
